/**
 * Single owner of `document.body.style.paddingBottom`.
 *
 * Multiple fixed, bottom-anchored elements (the destination sticky purchase
 * bar, the cookie consent banner) each need the page to reserve room at its
 * foot so they do not cover the last rows of content. A fixed element occupies
 * no space in flow, so without a reservation it paints over whatever is last on
 * the page — measured on Rome, over the footer's "Privacy inquiries" and
 * copyright rows.
 *
 * Two components writing `body.style.paddingBottom` directly race on the
 * transition between them: the consent banner owns the padding, the reader
 * accepts, the purchase bar's effect mounts and writes its height, and then the
 * banner's cleanup runs and restores the value it captured on mount — wiping
 * the bar's reservation. The bar's ResizeObserver does not re-fire, so the page
 * stays under-padded until the next resize.
 *
 * The fix is that no component touches `body` style. Each registers a named
 * contribution here; this module writes `max(contributions)` and removes the
 * property entirely once the registry empties, so the reservation is always the
 * tallest current occupant and never a stale captured value.
 *
 * Deliberately more than two mutually-exclusive elements strictly need: the
 * footer-coverage bug has now been introduced twice by two different
 * components, and a single owner makes a third occurrence impossible rather
 * than merely unlikely.
 */

const contributions = new Map<string, number>();

function apply(): void {
  if (typeof document === "undefined") {
    return;
  }

  let max = 0;
  for (const value of contributions.values()) {
    if (value > max) {
      max = value;
    }
  }

  if (max > 0) {
    document.body.style.paddingBottom = `${max}px`;
  } else {
    // Remove rather than set "0px": the property was ours, so an empty registry
    // must leave `body` exactly as it found it.
    document.body.style.removeProperty("padding-bottom");
  }
}

/**
 * Reserve `height` pixels at the foot of the page under `key`. Re-registering
 * the same key overwrites its previous contribution. A non-positive height
 * clears the key, same as {@link releaseBodyPaddingBottom}.
 */
export function setBodyPaddingBottom(key: string, height: number): void {
  if (height > 0) {
    contributions.set(key, height);
  } else {
    contributions.delete(key);
  }
  apply();
}

/**
 * Drop `key`'s contribution. When it was the last one the property is removed.
 */
export function releaseBodyPaddingBottom(key: string): void {
  contributions.delete(key);
  apply();
}
